import backupPower from "~/assets/photo/battery-only-proposal/backup-power.webp"
import powurCareBadge from "~/assets/photo/battery-only-proposal/powur-care-badge-battery.png"

import {
  Disclosure,
  Eyebrow,
  GradText,
  Icon,
  Panel,
  Section,
  SectionHeading,
  TINT_BG,
  TINT_TEXT,
  TINT_VAR,
} from "../primitives"
import {
  BACKUP_MODES,
  CARE_BENEFITS,
  CARE_BRANDS,
  CARE_COVERAGE,
} from "../static-copy"
import { useDesktopViewport } from "../use-desktop-viewport"

export function Outage(): JSX.Element {
  return (
    <Section id="backup">
      <SectionHeading
        eyebrow="Backup power"
        sub="Choose how you ride out an outage — stretch essentials for days, or run the whole home for hours."
      >
        What stays on when the <GradText>power&apos;s off</GradText>.
      </SectionHeading>
      <div className="relative overflow-hidden rounded-lg px-5 py-10 text-center lg:px-16 lg:py-16">
        <img
          src={backupPower}
          alt="Home battery providing backup power"
          loading="lazy"
          decoding="async"
          className="absolute inset-0 size-full object-cover"
        />
        <div className="pointer-events-none absolute inset-0 bg-[image:var(--bp-image-scrim)]" />
        <div className="pointer-events-none absolute inset-0 bg-bp-page/70" />
        <div className="relative mx-auto grid max-w-[960px] gap-10 lg:grid-cols-2 lg:gap-12">
          {BACKUP_MODES.map((mode, index) => (
            <div
              key={mode.title}
              className={`relative ${index > 0 ? `border-t border-bp-border/60 pt-10 lg:border-0 lg:pt-0` : ``}`}
            >
              <span
                aria-hidden="true"
                className={`font-display pointer-events-none absolute left-1/2 -translate-x-1/2 text-[6rem] leading-none font-semibold tabular-nums opacity-[0.08] lg:top-6 lg:text-[9rem] ${index > 0 ? `top-14` : `top-4`}`}
                style={{ color: TINT_VAR[mode.tint] }}
              >
                {index + 1}
              </span>
              <div className="relative">
                <div
                  className={`mx-auto mb-4 grid size-10 place-items-center rounded-full border border-bp-border-strong lg:mb-5 lg:size-11 ${TINT_TEXT[mode.tint]}`}
                >
                  <Icon name={mode.icon} className="size-5" />
                </div>
                <div
                  className={`font-display text-[length:var(--step-3)] leading-[1] font-semibold tracking-[-0.035em] tabular-nums lg:text-[length:var(--step-4)] ${TINT_TEXT[mode.tint]}`}
                >
                  {mode.runtime}
                </div>
                <Eyebrow className="mt-2">{mode.title}</Eyebrow>
                <span
                  aria-hidden="true"
                  className={`mx-auto mt-5 block h-px w-10 ${TINT_BG[mode.tint]}`}
                />
                <p className="mx-auto mt-5 flex max-w-[300px] flex-wrap justify-center gap-x-3 gap-y-1 text-[length:var(--step--1)] leading-relaxed text-bp-text-soft lg:max-w-[340px]">
                  {mode.items.map((item, itemIndex) => (
                    <span key={item} className="inline-flex items-center gap-3">
                      {itemIndex > 0 ? (
                        <span aria-hidden="true" className="text-bp-text-dim">
                          ·
                        </span>
                      ) : null}
                      {item}
                    </span>
                  ))}
                </p>
              </div>
            </div>
          ))}
        </div>
        <p className="relative mx-auto mt-10 max-w-[700px] text-[length:var(--step--2)] leading-relaxed text-bp-text-dim lg:mt-14">
          Runtime estimates are based on typical usage patterns. Your actual
          usage during outages will determine your total available runtime.
        </p>
      </div>
    </Section>
  )
}

function PowurCareBenefitList(): JSX.Element {
  return (
    <>
      {CARE_BENEFITS.map((benefit) => (
        <div
          key={benefit.title}
          className="grid grid-cols-[22px_1fr] items-start gap-3"
        >
          <div className="mt-0.5 grid size-5 place-items-center rounded-full bg-bp-accent text-bp-on-accent">
            <Icon name="check" className="size-3" />
          </div>
          <div>
            <div className="font-display text-[length:var(--step--1)] font-semibold text-bp-text">
              {benefit.title}
            </div>
            <div className="mt-0.5 text-[length:var(--step--2)] leading-snug text-bp-text-muted">
              {benefit.note}
            </div>
          </div>
        </div>
      ))}
    </>
  )
}

function PowurCareTerms(): JSX.Element {
  return (
    <>
      <p className="mt-5 max-w-[720px] text-[length:var(--step--2)] leading-relaxed text-bp-text-dim">
        Coverage begins on your installation date and runs continuously for 25
        years. See full terms at{` `}
        <a
          href="https://help.powur.com/hc/en-us/articles/52712651794971-Powur-Care-2-0"
          target="_blank"
          rel="noreferrer"
          className="font-semibold text-bp-accent"
        >
          powur.com/care
        </a>
        .
      </p>
      <p className="mt-2 max-w-[720px] text-[length:var(--step--2)] leading-relaxed text-bp-text-dim">
        This is a standalone product, independent and separate from Powur Care
        solar or solar+storage protection plans.
      </p>
    </>
  )
}

function PowurCareMobile(): JSX.Element {
  return (
    <>
      <Panel className="mb-2 flex items-center gap-4 px-5 py-4">
        <img
          src={powurCareBadge}
          alt="Powur Care 25-year protection"
          loading="lazy"
          decoding="async"
          className="h-16 w-auto object-contain"
        />
        <div>
          <div className="font-display text-[length:var(--step-0)] leading-snug font-semibold text-bp-text">
            25 years · $0 deductible
          </div>
          <div className="mt-0.5 text-[length:var(--step--2)] text-bp-text-muted">
            Claims handled in-house by Powur.
          </div>
        </div>
      </Panel>
      <div className="flex flex-col gap-2">
        <Disclosure
          defaultOpen
          summary={
            <span className="font-display text-[length:var(--step--1)] font-semibold text-bp-text">
              Industry-leading coverage
            </span>
          }
        >
          <div className="flex flex-col gap-3.5">
            <PowurCareBenefitList />
          </div>
        </Disclosure>
        <Disclosure
          summary={
            <span className="font-display text-[length:var(--step--1)] font-semibold text-bp-text">
              What we cover
              <span className="ml-2 rounded-full bg-bp-success-soft px-2 py-0.5 text-[length:var(--step--3)] font-bold tracking-wider text-bp-success uppercase">
                {CARE_COVERAGE.length} components
              </span>
            </span>
          }
        >
          <div className="flex flex-wrap gap-2">
            {CARE_COVERAGE.map((item) => (
              <span
                key={item.label}
                className="font-display inline-flex items-center gap-2 rounded-md border border-bp-border bg-bp-elevated px-3 py-2 text-[length:var(--step--1)] font-medium text-bp-text-soft"
              >
                <Icon name={item.icon} className="size-3.5 text-bp-accent" />
                {item.label}
              </span>
            ))}
          </div>
        </Disclosure>
        <Disclosure
          summary={
            <span className="font-display text-[length:var(--step--1)] font-semibold text-bp-text">
              All major brands
            </span>
          }
        >
          <p className="font-display text-[length:var(--step--1)] font-medium text-bp-text-soft">
            {CARE_BRANDS.join(` · `)}
          </p>
        </Disclosure>
      </div>
    </>
  )
}

export function PowurCare(): JSX.Element {
  const isDesktop = useDesktopViewport()

  if (!isDesktop) {
    return (
      <Section id="powur-care">
        <SectionHeading
          eyebrow="Powur Care"
          sub="Complete parts, labor, and battery replacement coverage. Powur handles claims in-house with zero deductible."
        >
          Protect your battery investment with{` `}
          <GradText>25-year protection.</GradText>
        </SectionHeading>
        <PowurCareMobile />
        <PowurCareTerms />
      </Section>
    )
  }

  return (
    <Section id="powur-care">
      <SectionHeading
        eyebrow="Powur Care"
        sub="Complete parts, labor, and battery replacement coverage. Powur handles claims in-house with zero deductible."
      >
        Protect your battery investment with{` `}
        <GradText>25-year protection.</GradText>
      </SectionHeading>
      <div className="grid overflow-hidden rounded-lg border border-bp-border bg-bp-surface lg:grid-cols-3">
        <div className="relative flex flex-col items-center justify-center gap-5 overflow-hidden border-b border-bp-border px-8 py-10 lg:border-r lg:border-b-0">
          <div className="pointer-events-none absolute inset-0 bg-[image:var(--bp-product-glow)]" />
          <img
            src={powurCareBadge}
            alt="Powur Care 25-year protection"
            loading="lazy"
            decoding="async"
            className="relative h-[220px] w-auto max-w-full object-contain"
          />
          <div className="relative max-w-[260px] text-center">
            <Eyebrow className="mb-2">Coverage period</Eyebrow>
            <div className="font-display text-[length:var(--step-1)] leading-snug font-semibold tracking-[-0.015em] text-bp-text">
              Two and a half decades of total battery protection.
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4 border-b border-bp-border px-8 py-10 lg:border-r lg:border-b-0">
          <div className="mb-1 flex items-center justify-between border-b border-bp-border pb-3.5">
            <Eyebrow>Industry-leading coverage</Eyebrow>
            <div className="rounded-full bg-bp-accent-soft px-2.5 py-0.5 text-[length:var(--step--3)] font-bold tracking-wider text-bp-accent uppercase">
              25 yrs
            </div>
          </div>
          <PowurCareBenefitList />
        </div>
        <div className="flex flex-col gap-3 px-8 py-10">
          <div className="mb-1 flex items-center justify-between border-b border-bp-border pb-3.5">
            <Eyebrow>What we cover</Eyebrow>
            <div className="rounded-full bg-bp-success-soft px-2.5 py-0.5 text-[length:var(--step--3)] font-bold tracking-wider text-bp-success uppercase">
              {CARE_COVERAGE.length} components
            </div>
          </div>
          {CARE_COVERAGE.map((item) => (
            <div
              key={item.label}
              className="grid grid-cols-[32px_1fr] items-center gap-3.5 rounded-md border border-bp-border bg-bp-elevated px-3.5 py-2.5"
            >
              <div className="grid size-7 place-items-center rounded-sm bg-bp-accent-soft text-bp-accent">
                <Icon name={item.icon} className="size-3.5" />
              </div>
              <span className="font-display text-[length:var(--step--1)] font-medium text-bp-text-soft">
                {item.label}
              </span>
            </div>
          ))}
          <div className="mt-auto border-t border-bp-border pt-3.5">
            <Eyebrow className="mb-1.5">All major brands</Eyebrow>
            <p className="font-display text-[length:var(--step--1)] font-medium text-bp-text-soft">
              {CARE_BRANDS.join(` · `)}
            </p>
          </div>
        </div>
      </div>
      <PowurCareTerms />
    </Section>
  )
}
